import React from 'react';
import { createPortal } from 'react-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Loader2, Check, Upload, Wand2, DollarSign, FileText, AlertTriangle, RefreshCw, ArrowLeft, WifiOff } from 'lucide-react';

const UploadProgress = ({ metadata = {}, progress = {}, costEstimate, onConfirm, onCancel, onReviewOutline, onSkipReview, onRetry, onReturnToDashboard }) => {
  const { title = 'Untitled', author = 'Unknown', wordCount, characterCount } = metadata;
  const { 
    stage = 'uploading', 
    percent = 0, 
    message = '', 
    awaitingConfirmation = false, 
    awaitingOutlineReview = false,
    degradedConnectivity = false  // NEW: Degraded connectivity state
  } = progress;

  const stages = [
    { key: 'uploading', label: 'Uploading', icon: Upload },
    { key: 'processing', label: 'Processing', icon: Loader2 },
    { key: 'awaiting_confirmation', label: 'Authorization', icon: DollarSign },
    { key: 'generating', label: 'Generating', icon: Wand2 },
    { key: 'complete', label: 'Complete', icon: Check }
  ];

  const currentStageIndex = stages.findIndex(s => s.key === stage);

  // Use portal to render outside any parent Dialog constraints
  const modalContent = (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <Card className="w-full max-w-md shadow-2xl bg-white max-h-[85vh] overflow-y-auto">
        <CardHeader className="p-3">
          <div className="flex items-center gap-2">
            <img 
              src="/newaudia-logo.jpg" 
              alt="NewAudia" 
              className="h-12 w-auto object-contain"
            />
            <CardTitle className="text-lg font-bold">Processing Manuscript</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-3 space-y-3">
          {/* Manuscript Metadata */}
          {metadata && Object.keys(metadata).length > 0 && (
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg p-2 space-y-1">
              <div className="text-sm font-bold text-gray-800 truncate">{title}</div>
              <div className="text-xs text-gray-600">
                <span className="font-medium">Author:</span> {author}
              </div>
              <div className="flex justify-between text-xs text-gray-600">
                {wordCount && (
                  <span><span className="font-medium">Words:</span> {wordCount}</span>
                )}
                {characterCount && (
                  <span><span className="font-medium">Chars:</span> {characterCount}</span>
                )}
              </div>
              {metadata.chapterCount !== undefined && metadata.chapterCount !== null && (
                <div className="flex items-center gap-1 text-xs text-gray-700 bg-white/50 rounded px-2 py-1 mt-1">
                  <FileText className="w-3 h-3" />
                  <span>
                    <span className="font-medium">Chapters:</span> {metadata.chapterCount === 1 ? '1 (continuous)' : metadata.chapterCount}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Progress Stages */}
          <div className="space-y-2">
            {stages.map((stageInfo, index) => {
              const Icon = stageInfo.icon;
              const isComplete = index < currentStageIndex;
              const isCurrent = index === currentStageIndex;
              const isPending = index > currentStageIndex;

              return (
                <div
                  key={stageInfo.key}
                  className={`flex items-center gap-2 p-2 rounded transition-all ${
                    isCurrent
                      ? 'bg-teal-50 border border-teal-500'
                      : isComplete
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-gray-50 border border-gray-200'
                  }`}
                >
                  <div
                    className={`flex-shrink-0 ${
                      isCurrent
                        ? 'text-teal-600'
                        : isComplete
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }`}
                  >
                    {isCurrent ? (
                      <Icon className="w-4 h-4 animate-spin" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div
                      className={`text-sm font-medium ${
                        isCurrent
                          ? 'text-teal-800'
                          : isComplete
                          ? 'text-green-800'
                          : 'text-gray-500'
                      }`}
                    >
                      {stageInfo.label}
                    </div>
                    {isCurrent && message && (
                      <div className="text-xs text-gray-600">{message}</div>
                    )}
                  </div>
                  {isComplete && (
                    <Check className="w-4 h-4 text-green-600" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Overall Progress Bar */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-gray-600">
              <span>Progress</span>
              <span className="font-semibold">{Math.round(percent)}%</span>
            </div>
            <Progress value={percent} className="h-2" />
          </div>

          {/* Outline Review Prompt */}
          {/* Outline review removed - users edit from Creator Console */}

          {/* TTS Authorization Modal */}
          {awaitingConfirmation && (
            <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4 space-y-4">
              <div className="text-center">
                <div className="bg-amber-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                  <DollarSign className="w-10 h-10 text-amber-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  ⚠️ Authorization Required
                </h3>
                <p className="text-sm text-gray-700 mb-3">
                  Text-to-Speech (TTS) generation will incur API costs. Please review and confirm before proceeding.
                </p>
                
                {costEstimate !== undefined && costEstimate !== null && (
                  <div className="bg-white rounded-lg p-3 border border-amber-200 mb-3">
                    <div className="text-xs text-gray-500 mb-1">Estimated Cost</div>
                    <div className="text-2xl font-bold text-amber-600">
                      ${(costEstimate || 0).toFixed(2)}
                    </div>
                    {characterCount && (
                      <div className="text-xs text-gray-500 mt-1">
                        {characterCount.toLocaleString()} characters
                      </div>
                    )}
                  </div>
                )}
                
                <div className="bg-red-50 border border-red-200 rounded p-2 mb-3">
                  <p className="text-xs text-red-700">
                    <strong>Note:</strong> Once started, TTS generation cannot be paused. You may cancel the upload entirely if needed.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={onCancel}
                  variant="outline"
                  className="px-6 py-2 text-sm border-gray-400"
                >
                  Cancel Upload
                </Button>
                <Button
                  onClick={onConfirm}
                  className="px-6 py-2 text-sm bg-green-600 hover:bg-green-700 text-white font-semibold"
                >
                  ✓ Authorize & Generate Audio
                </Button>
              </div>
            </div>
          )}

          {/* Status Message */}
          {!awaitingConfirmation && !degradedConnectivity && (
            <div className="text-center text-xs text-gray-600">
              {stage === 'complete'
                ? 'Ready to listen!'
                : stage === 'generating'
                ? 'Generating audio... Please wait.'
                : 'Processing...'}
            </div>
          )}
          
          {/* Degraded Connectivity State */}
          {degradedConnectivity && (
            <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4 space-y-4">
              <div className="text-center">
                <div className="bg-amber-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                  <WifiOff className="w-10 h-10 text-amber-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Connection Unstable
                </h3>
                <p className="text-sm text-gray-700 mb-3">
                  Unable to check TTS progress. <strong>Audio generation may still be processing</strong> on the server.
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  This does NOT mean the job failed. You can retry the connection or return to the dashboard to check later.
                </p>
              </div>
              
              <div className="flex gap-3 justify-center">
                {onReturnToDashboard && (
                  <Button
                    onClick={onReturnToDashboard}
                    variant="outline"
                    className="px-4 py-2 text-sm border-gray-400 flex items-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Return to Dashboard
                  </Button>
                )}
                {onRetry && (
                  <Button
                    onClick={onRetry}
                    className="px-4 py-2 text-sm bg-teal-600 hover:bg-teal-700 text-white font-semibold flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Retry Connection
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
  
  // Portal to document.body to escape Dialog constraints
  return createPortal(modalContent, document.body);
};

export default UploadProgress;
